import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/_services/api.service';
import {
  ExcelExportProperties,
  ExcelExportService,
  GridComponent,
  PdfExportProperties,
  PdfExportService,
  ResizeService,
  SortService,
  ToolbarService,
} from '@syncfusion/ej2-angular-grids';

interface ReportColumnDef {
  field: string;
  headerText: string;
  width?: number;
  type?: string;
  format?: string;
}

@Component({
  selector: 'app-monitor-report',
  templateUrl: './monitor-report.component.html',
  styleUrls: ['./monitor-report.component.css'],
  providers: [SortService, ToolbarService, PdfExportService, ExcelExportService, ResizeService],
})
export class MonitorReportComponent implements OnInit {
  @ViewChild('logtable') logtable: GridComponent;

  private readonly storageKey = 'monitor_report_columns_v1';
  private readonly defaultFields = ['created_at', 'sorgente', 'comando', 'riepilogo'];

  private readonly baseColumnDefs: ReportColumnDef[] = [
    { field: 'created_at', headerText: 'Data/ora', width: 160, type: 'date', format: 'd/M/y H:mm' },
    { field: 'sorgente', headerText: 'Sorgente', width: 120 },
    { field: 'comando', headerText: 'Comando', width: 100 },
    { field: 'comando_at', headerText: 'Data comando', width: 160, type: 'date', format: 'd/M/y H:mm' },
    { field: 'riepilogo', headerText: 'Riepilogo', width: 400 },
    { field: 'id', headerText: 'ID log', width: 80 },
    { field: 'monitor_id', headerText: 'ID monitor', width: 100 },
    { field: 'monitor_nome', headerText: 'Monitor', width: 160 },
  ];

  private readonly datiColumnLabels: Record<string, string> = {
    'dati.tipo_contatto': 'Tipo contatto',
    'dati.screenshot_richiesto': 'Screenshot richiesto',
    'dati.comando': 'Dati · Comando',
    'dati.comando_daemon': 'Comando daemon',
    'dati.comando_at': 'Dati · Data comando',
    'dati.device.script_version': 'Device · Versione script',
    'dati.device.ip': 'Device · IP',
    'dati.device.spazio_libero_gb': 'Device · Spazio libero (GB)',
    'dati.device.spazio_totale_gb': 'Device · Spazio totale (GB)',
    'dati.device.percentuale_usata': 'Device · % disco usato',
    'dati.device.cpu_temp': 'Device · CPU temp',
    'dati.device.uptime_minuti': 'Device · Uptime (min)',
    'dati.device.ram_totale_mb': 'Device · RAM totale (MB)',
    'dati.device.ram_libera_mb': 'Device · RAM libera (MB)',
    'dati.device.ram_usata_percentuale': 'Device · % RAM usata',
    'dati.device.modello': 'Device · Modello',
    'dati.device.marca': 'Device · Marca',
    'dati.device.android_version': 'Device · Android',
    'dati.device.build_id': 'Device · Build ID',
    'dati.device.hostname': 'Device · Hostname',
    'dati.device.load_avg': 'Device · Load avg',
    'dati.regola.id': 'Regola · ID',
    'dati.regola.nome': 'Regola · Nome',
    'dati.regola.playlist_id': 'Regola · Playlist ID',
  };

  monitorId: string;
  monitor: any = null;
  logList: any[] = [];
  toolbar = ['Search'];
  esteso = false;
  dallaData: Date = new Date(Date.now() - 7 * 86400000);
  allaData: Date = new Date();

  columnCatalog: ReportColumnDef[] = [];
  columnsAvailable: ReportColumnDef[] = [];
  columnsSelected: ReportColumnDef[] = [];
  visibleGridColumns: any[] = [];
  showColumnSettings = false;
  private savedFields: string[] = [];

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.monitorId = this.route.snapshot.paramMap.get('id');
    this.initColumnCatalog();
    this.loadColumnConfig();
    this.syncPickListFromConfig();
    this.applyColumnConfig();
    this.loadMonitor();
    this.loadLogs();
  }

  loadMonitor(): void {
    this.apiService
      .post('kodi/monitorslistall', { id: this.monitorId })
      .subscribe((data: any) => {
        if (data.Result && data.Result[0]) {
          this.monitor = data.Result[0];
        }
      });
  }

  loadLogs(): void {
    const params: any = {
      monitor_id: this.monitorId,
      esteso: this.esteso,
    };
    if (this.dallaData) {
      params.dallaData = this.formatApiDate(this.dallaData);
    }
    if (this.allaData) {
      params.allaData = this.formatApiDate(this.allaData);
    }

    this.apiService.post('kodi/loglistall', params).subscribe((data: any) => {
      this.logList = this.dedupeLogRows(this.normalizeLogRows(data.Result || []));
      this.discoverColumnsFromData(this.logList);
      this.rebuildSelectedColumns();
      this.syncPickListFromConfig();
      this.applyColumnConfig();
      if (this.logtable) {
        this.logtable.refresh();
      }
    });
  }

  formatApiDate(d: Date): string {
    const y = d.getFullYear();
    const m = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return y + '-' + m + '-' + day;
  }

  applicaFiltri(): void {
    this.loadLogs();
  }

  onEstesoChange(): void {
    this.syncPickListFromConfig();
    this.applyColumnConfig();
  }

  exportPdf(): void {
    if (!this.logtable || this.logList.length === 0) {
      return;
    }

    const props: PdfExportProperties = {
      dataSource: this.buildExportRows(),
      columns: this.buildExportColumns(),
      fileName: this.exportFileName('pdf'),
      pageOrientation: 'Landscape',
    };
    this.logtable.pdfExport(props);
  }

  exportExcel(): void {
    if (!this.logtable || this.logList.length === 0) {
      return;
    }

    const props: ExcelExportProperties = {
      dataSource: this.buildExportRows(),
      columns: this.buildExportColumns(),
      fileName: this.exportFileName('xlsx'),
    };
    this.logtable.excelExport(props);
  }

  openColumnSettings(): void {
    this.discoverColumnsFromData(this.logList);
    this.syncPickListFromConfig();
    this.showColumnSettings = true;
  }

  saveColumnSettings(): void {
    const selectedFields = this.columnsSelected.map((col) => col.field);
    if (this.esteso) {
      this.savedFields = selectedFields;
    } else {
      const preservedDati = this.savedFields.filter((field) => this.isDatiColumn(field));
      this.savedFields = [
        ...selectedFields.filter((field) => !this.isDatiColumn(field)),
        ...preservedDati,
      ];
    }
    localStorage.setItem(this.storageKey, JSON.stringify({ fields: this.savedFields }));
    this.rebuildSelectedColumns();
    this.syncPickListFromConfig();
    this.applyColumnConfig();
    this.showColumnSettings = false;
    if (this.logtable) {
      this.logtable.refresh();
    }
  }

  resetColumnDefaults(): void {
    localStorage.removeItem(this.storageKey);
    this.savedFields = [...this.defaultFields];
    this.rebuildSelectedColumns();
    this.syncPickListFromConfig();
    this.applyColumnConfig();
    if (this.logtable) {
      this.logtable.refresh();
    }
  }

  private initColumnCatalog(): void {
    this.columnCatalog = this.baseColumnDefs.map((col) => ({ ...col }));
    Object.keys(this.datiColumnLabels).forEach((field) => {
      if (!this.columnCatalog.find((col) => col.field === field)) {
        this.columnCatalog.push(this.createDatiColumnDef(field));
      }
    });
    this.sortColumnCatalog();
  }

  private loadColumnConfig(): void {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      this.savedFields = [...this.defaultFields];
      this.rebuildSelectedColumns();
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.savedFields = Array.isArray(parsed?.fields) ? parsed.fields : [...this.defaultFields];
    } catch {
      this.savedFields = [...this.defaultFields];
    }

    this.rebuildSelectedColumns();
  }

  private rebuildSelectedColumns(): void {
    this.columnsSelected = this.savedFields
      .map((field) => this.findColumnDef(field))
      .filter((col) => !!col);

    if (this.columnsSelected.length === 0) {
      this.savedFields = [...this.defaultFields];
      this.columnsSelected = this.savedFields
        .map((field) => this.findColumnDef(field))
        .filter((col) => !!col);
    }
  }

  private syncPickListFromConfig(): void {
    const allSelected = this.savedFields
      .map((field) => this.findColumnDef(field))
      .filter((col) => !!col);

    if (this.esteso) {
      this.columnsSelected = allSelected;
      const selectedFields = new Set(this.columnsSelected.map((col) => col.field));
      this.columnsAvailable = this.columnCatalog.filter((col) => !selectedFields.has(col.field));
      return;
    }

    this.columnsSelected = allSelected.filter((col) => !this.isDatiColumn(col.field));
    const selectedFields = new Set(this.columnsSelected.map((col) => col.field));
    this.columnsAvailable = this.columnCatalog.filter(
      (col) => !selectedFields.has(col.field) && !this.isDatiColumn(col.field)
    );
  }

  private getDisplayColumns(): ReportColumnDef[] {
    if (this.esteso) {
      return this.columnsSelected;
    }
    return this.columnsSelected.filter((col) => !this.isDatiColumn(col.field));
  }

  private isDatiColumn(field: string): boolean {
    return field.startsWith('dati.');
  }

  private applyColumnConfig(): void {
    this.visibleGridColumns = this.getDisplayColumns().map((col) => {
      const field = col.field;
      const columnWidth = this.columnWidthForHeader(col.headerText, col.width);
      const gridCol: any = {
        field,
        headerText: col.headerText,
        width: columnWidth,
        minWidth: columnWidth,
        clipMode: 'EllipsisWithTooltip',
        valueAccessor: (_f: string, data: object) => {
          return this.formatCellValue(this.getFieldValue(data, field), col);
        },
      };
      if (col.type === 'date') {
        gridCol.type = 'date';
        gridCol.format = col.format || 'd/M/y H:mm';
      }
      return gridCol;
    });
    this.refreshGridColumns();
  }

  private refreshGridColumns(): void {
    if (!this.logtable) {
      return;
    }
    this.logtable.columns = this.visibleGridColumns;
    this.logtable.refreshColumns();
    this.logtable.refresh();
  }

  private discoverColumnsFromData(rows: any[]): void {
    const discovered = new Set<string>();

    rows.forEach((row) => {
      const dati = this.parseDati(row?.dati);
      if (dati) {
        this.collectDatiPaths(dati, 'dati', discovered);
      }
    });

    discovered.forEach((field) => {
      if (!this.columnCatalog.find((col) => col.field === field)) {
        this.columnCatalog.push(this.createDatiColumnDef(field));
      }
    });

    this.sortColumnCatalog();
  }

  private normalizeLogRows(rows: any[]): any[] {
    return rows.map((row) => {
      const copy = { ...row };
      const dati = this.parseDati(copy.dati);
      copy.dati = dati;
      if (dati) {
        this.flattenDatiOntoRow(dati, 'dati', copy);
      }
      return copy;
    });
  }

  /** Evita doppioni identici (stesso orario/sorgente/comando/riepilogo). */
  private dedupeLogRows(rows: any[]): any[] {
    const seen = new Set<string>();
    const out: any[] = [];

    rows.forEach((row) => {
      const key = [
        row?.created_at || '',
        row?.sorgente || '',
        row?.comando || '',
        row?.riepilogo || '',
      ].join('|');

      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      out.push(row);
    });

    return out;
  }

  private flattenDatiOntoRow(value: any, prefix: string, target: any): void {
    if (value === null || value === undefined) {
      return;
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
      target[prefix] = value;
      return;
    }
    Object.keys(value).forEach((key) => {
      const path = prefix + '.' + key;
      const child = value[key];
      if (child !== null && typeof child === 'object' && !Array.isArray(child)) {
        this.flattenDatiOntoRow(child, path, target);
      } else {
        target[path] = child;
      }
    });
  }

  private parseDati(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  }

  private sortColumnCatalog(): void {
    this.columnCatalog.sort((a, b) => {
      const aDati = a.field.startsWith('dati.') ? 1 : 0;
      const bDati = b.field.startsWith('dati.') ? 1 : 0;
      if (aDati !== bDati) {
        return aDati - bDati;
      }
      return a.headerText.localeCompare(b.headerText, 'it');
    });
  }

  private collectDatiPaths(value: any, prefix: string, out: Set<string>): void {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value !== 'object' || Array.isArray(value)) {
      if (prefix.startsWith('dati.')) {
        out.add(prefix);
      }
      return;
    }

    Object.keys(value).forEach((key) => {
      const path = prefix + '.' + key;
      const child = value[key];
      if (child !== null && typeof child === 'object' && !Array.isArray(child)) {
        this.collectDatiPaths(child, path, out);
      } else if (child === null || typeof child !== 'object') {
        out.add(path);
      }
    });
  }

  private createDatiColumnDef(field: string): ReportColumnDef {
    const def: ReportColumnDef = {
      field,
      headerText: this.fieldToHeader(field),
      width: 140,
    };

    if (field.endsWith('_at') || field.endsWith('_date')) {
      def.type = 'date';
      def.format = 'd/M/y H:mm';
    }

    return def;
  }

  private fieldToHeader(field: string): string {
    if (this.datiColumnLabels[field]) {
      return this.datiColumnLabels[field];
    }

    const parts = field.split('.');
    return parts
      .map((part) => part.replace(/_/g, ' '))
      .join(' · ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private findColumnDef(field: string): ReportColumnDef | null {
    const found = this.columnCatalog.find((col) => col.field === field);
    return found ? { ...found } : null;
  }

  private buildExportColumns(): any[] {
    return this.getDisplayColumns().map((col) => ({
      field: this.exportFieldName(col.field),
      headerText: col.headerText,
      width: this.columnWidthForHeader(col.headerText, col.width),
    }));
  }

  private buildExportRows(): any[] {
    return this.logList.map((row) => {
      const out: any = {};
      this.getDisplayColumns().forEach((col) => {
        out[this.exportFieldName(col.field)] = this.formatExportValue(
          this.getFieldValue(row, col.field),
          col
        );
      });
      return out;
    });
  }

  private exportFieldName(field: string): string {
    return field.replace(/\./g, '__');
  }

  private formatExportValue(value: any, col: ReportColumnDef): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (col.type === 'date') {
      const d = value instanceof Date ? value : new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString('it-IT');
      }
    }
    return String(value);
  }

  private getFieldValue(row: any, field: string): any {
    if (!row) {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(row, field)) {
      return row[field];
    }

    const nested = field.split('.').reduce((obj, key) => (obj != null ? obj[key] : null), row);
    if (nested !== null && nested !== undefined) {
      return nested;
    }

    return null;
  }

  private formatCellValue(value: any, col: ReportColumnDef): any {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (col.type === 'date') {
      const d = value instanceof Date ? value : new Date(value);
      return isNaN(d.getTime()) ? value : d;
    }
    return value;
  }

  private exportFileName(ext: string): string {
    const monitor = (this.monitor?.descrizione || this.monitor?.nome || 'monitor')
      .replace(/[^\w\-]+/g, '_')
      .substring(0, 40);
    const dal = this.dallaData ? this.formatApiDate(this.dallaData) : '';
    const al = this.allaData ? this.formatApiDate(this.allaData) : '';
    return `report_monitor_${monitor}_${dal}_${al}.${ext}`;
  }

  private columnWidthForHeader(headerText: string, preferred?: number): number {
    const fromHeader = headerText.length * 9 + 40;
    return Math.max(preferred || 0, fromHeader, 110);
  }
}
